import { gsap } from 'gsap'
import { getTextFadeAnimation } from './textFade'
import { getTextScaleAnimation } from './textScale'
import { getTextRotateAnimation } from './textRotate'
import { getTextBlurAnimation } from './textBlur'
import { getTextHighlightAnimation } from './textHighlight'
import { getTextColorSwitchAnimation } from './textColorSwitch'
import { getTextTypewriterAnimation } from './textTypewriter'
import { getTextTargets, onTextResplit, prepareTextSplit, resolveSplitType } from './splitText'

/**
 * Text animations animate the fragments (chars, words or lines) of a text element with a stagger
 * instead of the element itself, so they do not fit the `timeline.from(element, vars)` shape of
 * the other animations. `getTextAnimation` describes the tween and `addTextAnimation` puts it on
 * the timeline.
 *
 * Settings (published by dashboard `traits/Animations.php`):
 * - `split`: 'chars' | 'words' | 'lines', default words
 * - `stagger`: 0..1, the share of `duration` over which the fragment starts are spread; 0 animates
 *   all fragments together, 1 is a pure sequence. The whole animation always takes `duration`.
 * - the type's own fields (`start_position`, `blur`, `zoom`, `rotation`, `color`)
 *
 * A type describes its start state in `from`, like the element animations: an in-animation is a
 * `from()` towards the element's own styling, an out-animation a `to()` away from it. Only the
 * highlight builds its own `sequence` tween instead.
 */

const BUILDERS = {
    textFade: getTextFadeAnimation,
    textScale: getTextScaleAnimation,
    textRotate: getTextRotateAnimation,
    textBlur: getTextBlurAnimation,
    textHighlight: getTextHighlightAnimation,
    textColorSwitch: getTextColorSwitchAnimation,
    textTypewriter: getTextTypewriterAnimation,
}

const DEFAULT_STAGGER = 0.5
const MIN_TWEEN_DURATION = 0.01
const DIRECTIONS = ['in', 'out']

export const TEXT_ANIMATION_TYPES = Object.keys(BUILDERS)

export function isTextAnimationType (type) {
    return Object.hasOwn(BUILDERS, type)
}

function textDirections (types) {
    return DIRECTIONS.filter(direction => types[direction] && isTextAnimationType(types[direction].type))
}

export function hasTextAnimation (types) {
    return textDirections(types).length > 0
}

// Split once for both directions: a second split would orphan the tweens of the first.
export function prepareTextAnimations (el, types) {
    prepareTextSplit(el, textDirections(types).map(direction => resolveSplitType(types[direction])))
}

function prefersReducedMotion () {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function getTextAnimation (element, settings, direction) {
    const stagger = Math.min(Math.max(Number(settings.stagger ?? DEFAULT_STAGGER), 0), 1)
    return {
        text: true,
        split: resolveSplitType(settings),
        // Reduced motion keeps the effect but moves the text as one block.
        stagger: prefersReducedMotion() ? 0 : stagger,
        // For the call sites that need a plain tween (a zero-duration toggle under a trigger).
        fallback: { opacity: 0 },
        ...BUILDERS[settings.type](element, settings, direction),
    }
}

function buildTween (el, animation, duration, direction) {
    const targets = getTextTargets(el, animation.split)
    if (targets.length === 0) {
        return null
    }
    if (animation.sequence) {
        return animation.sequence(targets, duration, direction)
    }

    // The last fragment must finish at `duration`, where the timeline fires its done callback and
    // clears the fragments, so the stagger spreads the starts over what the tween itself leaves.
    const tweenDuration = animation.instant ? MIN_TWEEN_DURATION : Math.max(duration * (1 - animation.stagger), MIN_TWEEN_DURATION)
    const amount = Math.max(duration - tweenDuration, 0)
    const vars = {
        ...animation.from,
        duration: tweenDuration,
        ease: animation.ease,
        force3D: animation.force3D,
        stagger: { amount, from: 'start' },
    }
    return direction === 'out' ? gsap.to(targets, vars) : gsap.from(targets, vars)
}

/**
 * Add the text tween of `animation` to `timeline` at `position`, and keep it there across a
 * re-split: the old tween is removed and a new one targets the new fragments at the same spot.
 */
export function addTextAnimation (timeline, element, animation, duration, position, direction) {
    const el = element[0]
    let tween = buildTween(el, animation, duration, direction)
    if (!tween) {
        return false
    }
    timeline.add(tween, position)

    onTextResplit(el, () => {
        timeline.remove(tween)
        tween = buildTween(el, animation, duration, direction)
        if (tween) {
            timeline.add(tween, position)
            timeline.time(timeline.time(), true)
        }
    })
    return true
}
